/*
 * newsbot - Category add/edit form using SlideOver pattern
 */

import { SlideOver } from "@components/panels";
import { TextField, NumberField, TextArea, SwitchGroup } from "@components/inputs";
import type { CategoryWithStats } from "@/types";

interface CategoryFormValues {
  name: string;
  slug: string;
  keywords: string;
  priority: number;
  enabled: boolean;
}

interface CategoryAddFormProps {
  isOpen: boolean;
  toggle: () => void;
  category?: CategoryWithStats | null;
  onSubmit: (values: CategoryFormValues) => void;
  onDelete?: () => void;
}

// Manual validation function (matching autobrr pattern - no Zod)
const validateCategory = (values: CategoryFormValues) => {
  const errors: Partial<Record<keyof CategoryFormValues, string>> = {};

  if (!values.name || values.name.trim().length === 0) {
    errors.name = "Name is required";
  } else if (values.name.length > 100) {
    errors.name = "Name must be 100 characters or less";
  }

  if (!values.slug || values.slug.trim().length === 0) {
    errors.slug = "Slug is required";
  } else if (values.slug.length > 100) {
    errors.slug = "Slug must be 100 characters or less";
  } else if (!/^[a-z0-9-]+$/.test(values.slug)) {
    errors.slug = "Slug must contain only lowercase letters, numbers, and hyphens";
  }

  if (values.priority < 0 || values.priority > 1000) {
    errors.priority = "Priority must be between 0 and 1000";
  }

  return errors;
};

export function CategoryAddForm({
  isOpen,
  toggle,
  category,
  onSubmit,
  onDelete,
}: CategoryAddFormProps) {
  const isEditing = !!category;
  const isSystemCategory = category && category.user_id === null;

  const initialValues: CategoryFormValues = {
    name: category?.name ?? "",
    slug: category?.slug ?? "",
    keywords: category?.keywords?.join(", ") ?? "",
    priority: category?.priority ?? 50,
    enabled: category?.enabled ?? true,
  };

  const handleSubmit = (values?: CategoryFormValues) => {
    if (values) {
      onSubmit(values);
    }
  };

  return (
    <SlideOver<CategoryFormValues>
      title="Category"
      type={isEditing ? "UPDATE" : "CREATE"}
      isOpen={isOpen}
      toggle={toggle}
      initialValues={initialValues}
      validate={validateCategory}
      onSubmit={handleSubmit}
      deleteAction={isSystemCategory ? undefined : onDelete}
    >
      {(_values) => (
        <div className="py-6 space-y-6 sm:py-0 sm:space-y-0 sm:divide-y sm:divide-gray-200 dark:sm:divide-gray-700">
          {isSystemCategory && (
            <div className="mx-4 my-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                This is a system default category. Saving will create a copy that overrides it for your account.
              </p>
            </div>
          )}

          <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
            <TextField
              name="name"
              label="Name"
              required
              placeholder="e.g., Bitcoin News"
            />
          </div>

          <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
            <TextField
              name="slug"
              label="Slug"
              required
              placeholder="e.g., bitcoin-news"
            />
          </div>

          <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
            <TextArea
              name="keywords"
              label="Keywords"
              placeholder="Bitcoin, BTC, $BTC"
              rows={3}
            />
          </div>

          <div className="space-y-1 px-4 sm:space-y-0 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-6 sm:py-5">
            <NumberField
              name="priority"
              label="Priority"
              placeholder="50"
              min={0}
              max={1000}
            />
          </div>

          <div className="space-y-1 px-4 sm:space-y-0 sm:px-6 sm:py-5">
            <SwitchGroup
              name="enabled"
              label="Enabled"
              description="When enabled, this category will be used for story categorization"
            />
          </div>
        </div>
      )}
    </SlideOver>
  );
}
